import { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { defineModal } from '@/lib/modals';
import { useDropzone } from 'react-dropzone';
import { useForm, useStore } from '@tanstack/react-form';
import { Image as ImageIcon, Maximize2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import WYSIWYGEditor from '@/components/ui/wysiwyg';
import type { LocalImageMetadata } from '@/components/ui/wysiwyg/context/task-attempt-context';
import BranchSelector from '@/components/tasks/BranchSelector';
import RepoBranchSelector from '@/components/tasks/RepoBranchSelector';
import { ExecutorProfileSelector } from '@/components/settings';
import { useUserSystem } from '@/components/ConfigProvider';
import {
  useTaskImages,
  useImageUpload,
  useTaskMutations,
  useProjectRepos,
  useRepoBranchSelection,
  useProjectColumns,
} from '@/hooks';
import { usersApi, localAuthApi } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import {
  useKeySubmitTask,
  useKeySubmitTaskAlt,
  useKeyExit,
  Scope,
} from '@/keyboard';
import { useHotkeysContext } from 'react-hotkeys-hook';
import { cn } from '@/lib/utils';
import type {
  TaskStatus,
  ExecutorProfileId,
  ImageResponse,
} from 'shared/types';
import { DescriptionExpandDialog } from './DescriptionExpandDialog';

interface Task {
  id: string;
  project_id: string;
  title: string;
  description: string | null;
  status: TaskStatus;
  column_id: string | null;
  created_at: string;
  updated_at: string;
}

export type TaskFormDialogProps =
  | { mode: 'create'; projectId: string; initialColumnId?: string }
  | { mode: 'edit'; projectId: string; task: Task }
  | { mode: 'duplicate'; projectId: string; initialTask: Task }
  | {
      mode: 'subtask';
      projectId: string;
      parentTaskAttemptId: string;
      initialBaseBranch: string;
    };

type RepoBranch = { repoId: string; branch: string };

type TaskFormValues = {
  title: string;
  description: string;
  status: TaskStatus;
  column_id: string | null;
  executorProfileId: ExecutorProfileId | null;
  repoBranches: RepoBranch[];
  autoStart: boolean;
  assigned_user_id: number | null;
};

const TaskFormDialogImpl = NiceModal.create<TaskFormDialogProps>((props) => {
  const { mode, projectId } = props;
  const editMode = mode === 'edit';
  const modal = useModal();
  const { t } = useTranslation(['tasks', 'common']);
  const { createTask, createAndStart, updateTask } =
    useTaskMutations(projectId);
  const { system, profiles, loading: userSystemLoading } = useUserSystem();
  const { upload, uploadForTask } = useImageUpload();
  const { enableScope, disableScope } = useHotkeysContext();

  // Local UI state
  const [images, setImages] = useState<ImageResponse[]>([]);
  const [newlyUploadedImageIds, setNewlyUploadedImageIds] = useState<string[]>(
    []
  );
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);
  const [expandDialogOpen, setExpandDialogOpen] = useState(false);
  const forceCreateOnlyRef = useRef(false);

  const { data: taskImages } = useTaskImages(
    editMode ? props.task.id : undefined
  );
  const { data: projectRepos = [] } = useProjectRepos(projectId, {
    enabled: modal.visible,
  });
  const initialBranch =
    mode === 'subtask' ? props.initialBaseBranch : undefined;
  const {
    configs: repoBranchConfigs,
    isLoading: branchesLoading,
    selectedRepoIds,
    toggleRepo,
    selectAll,
    selectNone,
  } = useRepoBranchSelection({
    repos: projectRepos,
    initialBranch,
    enabled: modal.visible && projectRepos.length > 0,
  });

  // Fetch project columns
  const { data: projectColumns = [], isLoading: columnsLoading } =
    useProjectColumns(projectId);

  // Fetch users for assignment
  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: usersApi.list,
    enabled: modal.visible,
  });

  // Get current user for permission checks
  const { data: _currentUser } = useQuery({
    queryKey: ['localUser'],
    queryFn: localAuthApi.getCurrentUser,
    enabled: modal.visible,
  });

  const defaultRepoBranches = useMemo((): RepoBranch[] => {
    return repoBranchConfigs
      .filter((c) => c.targetBranch !== null)
      .map((c) => ({ repoId: c.repoId, branch: c.targetBranch! }));
  }, [repoBranchConfigs]);

  // Get default form values based on mode
  const defaultValues = useMemo((): TaskFormValues => {
    const baseProfile = system.config?.executor_profile || null;

    switch (mode) {
      case 'edit':
        return {
          title: props.task.title,
          description: props.task.description || '',
          status: props.task.status,
          column_id: props.task.column_id,
          executorProfileId: baseProfile,
          repoBranches: defaultRepoBranches,
          autoStart: false,
          assigned_user_id: (props.task as any).assigned_user_id || null,
        };

      case 'duplicate':
        return {
          title: props.initialTask.title,
          description: props.initialTask.description || '',
          status: 'todo',
          column_id: props.initialTask.column_id,
          executorProfileId: baseProfile,
          repoBranches: defaultRepoBranches,
          autoStart: true,
          assigned_user_id: null,
        };

      case 'subtask':
      case 'create':
      default:
        // For create mode, use initialColumnId if provided
        const initialColumnId =
          mode === 'create' ? props.initialColumnId : null;
        return {
          title: '',
          description: '',
          status: 'todo',
          column_id: initialColumnId || null,
          executorProfileId: baseProfile,
          repoBranches: defaultRepoBranches,
          autoStart: true,
          assigned_user_id: null,
        };
    }
  }, [mode, props, system.config?.executor_profile, defaultRepoBranches]);

  // Form submission handler
  const handleSubmit = async ({ value }: { value: TaskFormValues }) => {
    if (editMode) {
      await updateTask.mutateAsync(
        {
          taskId: props.task.id,
          data: {
            title: value.title,
            description: value.description,
            status: value.status,
            column_id: value.column_id,
            parent_workspace_id: null,
            image_ids: images.length > 0 ? images.map((img) => img.id) : null,
            assigned_user_id: value.assigned_user_id as any,
          },
        },
        { onSuccess: () => modal.remove() }
      );
    } else {
      const imageIds =
        newlyUploadedImageIds.length > 0 ? newlyUploadedImageIds : null;
      const task = {
        project_id: projectId,
        title: value.title,
        description: value.description,
        status: null,
        column_id: value.column_id ?? undefined,
        parent_workspace_id:
          mode === 'subtask' ? props.parentTaskAttemptId : null,
        image_ids: imageIds,
        shared_task_id: null,
        assigned_user_id: value.assigned_user_id as any,
      };
      const shouldAutoStart = value.autoStart && !forceCreateOnlyRef.current;
      if (shouldAutoStart) {
        // Filter repos by selection - only include selected repos
        const repos = value.repoBranches
          .filter((rb) => selectedRepoIds.has(rb.repoId))
          .map((rb) => ({
            repo_id: rb.repoId,
            target_branch: rb.branch,
          }));
        // Prevent submission if no repos selected
        if (repos.length === 0) {
          return;
        }
        await createAndStart.mutateAsync(
          {
            task,
            executor_profile_id: value.executorProfileId!,
            repos,
          },
          { onSuccess: () => modal.remove() }
        );
      } else {
        await createTask.mutateAsync(task, { onSuccess: () => modal.remove() });
      }
    }
  };

  const validator = (value: TaskFormValues): string | undefined => {
    if (!value.title.trim().length) return 'need title';
    if (value.autoStart && !forceCreateOnlyRef.current) {
      if (!value.executorProfileId) return 'need executor profile';
      if (
        value.repoBranches.length === 0 ||
        value.repoBranches.some((rb) => !rb.branch)
      ) {
        return 'need branch for all repos';
      }
    }
  };

  // Initialize TanStack Form
  const form = useForm({
    defaultValues: defaultValues,
    onSubmit: handleSubmit,
    validators: {
      // we use an onMount validator so that the primary action button can
      // enable/disable itself based on `canSubmit`
      onMount: ({ value }) => validator(value),
      onChange: ({ value }) => validator(value),
    },
  });

  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const isDirty = useStore(form.store, (state) => state.isDirty);
  const canSubmit = useStore(form.store, (state) => state.canSubmit);

  // Load images for edit mode
  useEffect(() => {
    if (!taskImages) return;
    setImages(taskImages);
  }, [taskImages]);

  const onDrop = useCallback(
    async (files: File[]) => {
      for (const file of files) {
        try {
          // In edit mode, use uploadForTask to associate immediately
          // In create mode, use plain upload (will associate on task creation)
          const img = editMode
            ? await uploadForTask(props.task.id, file)
            : await upload(file);

          // Add markdown image reference to description
          const markdownText = `![${img.original_name}](${img.file_path})`;
          form.setFieldValue('description', (prev) =>
            prev.trim() === '' ? markdownText : `${prev} ${markdownText}`
          );
          setImages((prev) => [...prev, img]);
          setNewlyUploadedImageIds((prev) => [...prev, img.id]);
        } catch {
          // Silently ignore upload errors for now
        }
      }
    },
    [editMode, props, upload, uploadForTask, form]
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: dropzoneOpen,
  } = useDropzone({
    onDrop: onDrop,
    accept: { 'image/*': [] },
    disabled: isSubmitting,
    noClick: true,
    noKeyboard: true,
  });

  // Compute localImages for WYSIWYG rendering of uploaded images
  const localImages: LocalImageMetadata[] = useMemo(
    () =>
      images.map((img) => ({
        path: img.file_path,
        proxy_url: `/api/images/${img.id}/file`,
        file_name: img.original_name,
        size_bytes: Number(img.size_bytes),
        format: img.mime_type?.split('/')[1] ?? 'png',
      })),
    [images]
  );

  // Handler to open expand description dialog
  const handleOpenExpandDialog = useCallback(
    async (
      currentDescription: string,
      onChangeDescription: (value: string) => void
    ) => {
      // Hide main dialog while expand dialog is open
      setExpandDialogOpen(true);

      const result = await DescriptionExpandDialog.show({
        value: currentDescription,
        onChange: onChangeDescription,
        projectId,
        taskId: editMode
          ? (props as { mode: 'edit'; task: Task }).task?.id
          : undefined,
        localImages,
        onPasteFiles: onDrop,
        disabled: isSubmitting,
        editMode,
      });

      // Restore main dialog when expand dialog closes
      setExpandDialogOpen(false);
      return result;
    },
    [projectId, editMode, props, localImages, onDrop, isSubmitting]
  );

  // Unsaved changes detection
  const hasUnsavedChanges = useCallback(() => {
    if (isDirty) return true;
    if (newlyUploadedImageIds.length > 0) return true;
    if (images.length > 0 && !editMode) return true;
    return false;
  }, [isDirty, newlyUploadedImageIds, images, editMode]);

  // beforeunload listener
  useEffect(() => {
    if (!modal.visible || isSubmitting) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [modal.visible, isSubmitting, hasUnsavedChanges]);

  // Keyboard shortcuts
  const primaryAction = useCallback(() => {
    if (isSubmitting || !canSubmit) return;
    void form.handleSubmit();
  }, [form, isSubmitting, canSubmit]);

  const shortcutsEnabled =
    modal.visible && !isSubmitting && canSubmit && !showDiscardWarning;

  useKeySubmitTask(primaryAction, {
    enabled: shortcutsEnabled,
    scope: Scope.DIALOG,
    enableOnFormTags: ['input', 'INPUT', 'textarea', 'TEXTAREA'],
    preventDefault: true,
  });

  const canSubmitAlt = useStore(
    form.store,
    (state) => state.values.title.trim().length > 0 && !state.isSubmitting
  );

  const handleSubmitCreateOnly = useCallback(() => {
    forceCreateOnlyRef.current = true;
    const promise = form.handleSubmit();
    Promise.resolve(promise).finally(() => {
      forceCreateOnlyRef.current = false;
    });
  }, [form]);

  useKeySubmitTaskAlt(handleSubmitCreateOnly, {
    enabled: modal.visible && canSubmitAlt && !showDiscardWarning,
    scope: Scope.DIALOG,
    enableOnFormTags: ['input', 'INPUT', 'textarea', 'TEXTAREA'],
    preventDefault: true,
  });

  // Dialog close handling
  const handleDialogClose = (open: boolean) => {
    if (open) return;
    if (hasUnsavedChanges()) {
      setShowDiscardWarning(true);
    } else {
      modal.remove();
    }
  };

  const handleDiscardChanges = () => {
    form.reset();
    setImages([]);
    setNewlyUploadedImageIds([]);
    setShowDiscardWarning(false);
    modal.remove();
  };

  const handleContinueEditing = () => {
    setShowDiscardWarning(false);
  };

  // Manage CONFIRMATION scope when warning is shown
  useEffect(() => {
    if (showDiscardWarning) {
      disableScope(Scope.DIALOG);
      enableScope(Scope.CONFIRMATION);
    } else {
      disableScope(Scope.CONFIRMATION);
      enableScope(Scope.DIALOG);
    }
  }, [showDiscardWarning, enableScope, disableScope]);

  useKeyExit(handleContinueEditing, {
    scope: Scope.CONFIRMATION,
    when: () => modal.visible && showDiscardWarning,
  });

  const loading = branchesLoading || userSystemLoading || columnsLoading;
  if (loading) return <></>;

  return (
    <>
      <Dialog
        open={modal.visible && !expandDialogOpen}
        onOpenChange={handleDialogClose}
        uncloseable={showDiscardWarning}
      >
        <div
          {...getRootProps()}
          className="h-full flex flex-col gap-4 p-4 relative min-h-0"
        >
          <input {...getInputProps()} />
          {/* Drag overlay */}
          {isDragActive && (
            <div className="absolute inset-0 z-50 bg-primary/95 border-2 border-dashed border-primary-foreground/50 rounded-lg flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <ImageIcon className="h-12 w-12 mx-auto mb-2 text-primary-foreground" />
                <p className="text-lg font-medium text-primary-foreground">
                  {t('taskFormDialog.dropImagesHere')}
                </p>
              </div>
            </div>
          )}

          {/* Title */}
          <form.Field name="title">
            {(field) => (
              <Input
                id="task-title"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('taskFormDialog.titlePlaceholder')}
                disabled={isSubmitting}
                className="text-base"
                autoFocus
              />
            )}
          </form.Field>

          {/* Description */}
          <form.Field name="description">
            {(field) => (
              <div className="border p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">
                    {t('taskFormDialog.descriptionPlaceholder')}
                  </Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      handleOpenExpandDialog(field.state.value, (value) =>
                        field.handleChange(value)
                      )
                    }
                    disabled={isSubmitting}
                    className="h-7 px-2 text-xs"
                    title={t('taskFormDialog.expandButton.tooltip')}
                  >
                    <Maximize2 className="h-3.5 w-3.5 mr-1" />
                    {t('taskFormDialog.expandButton.label')}
                  </Button>
                </div>
                <WYSIWYGEditor
                  placeholder={t('taskFormDialog.descriptionPlaceholder')}
                  className="w-full h-24 overflow-auto"
                  value={field.state.value}
                  onChange={(desc) => field.handleChange(desc)}
                  disabled={isSubmitting}
                  projectId={projectId}
                  onPasteFiles={onDrop}
                  onCmdEnter={primaryAction}
                  onShiftCmdEnter={handleSubmitCreateOnly}
                  taskId={editMode ? props.task.id : undefined}
                  localImages={localImages}
                />
              </div>
            )}
          </form.Field>

          {/* Assigned User */}
          <form.Field name="assigned_user_id">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor="assigned-user" className="text-sm font-medium">
                  Assign to Developer
                </Label>
                <Select
                  value={
                    field.state.value ? String(field.state.value) : 'unassigned'
                  }
                  onValueChange={(value) =>
                    field.handleChange(
                      value === 'unassigned' ? null : Number(value)
                    )
                  }
                  disabled={isSubmitting}
                >
                  <SelectTrigger id="assigned-user">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={String(user.id)}>
                        {user.username} ({user.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Only the assigned user and admins can start this task
                </p>
              </div>
            )}
          </form.Field>

          {/* Edit mode status */}
          {editMode && (
            <form.Field name="status">
              {(field) => (
                <div className="space-y-2">
                  <Label htmlFor="task-status" className="text-sm font-medium">
                    {t('taskFormDialog.statusLabel')}
                  </Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(value) =>
                      field.handleChange(value as TaskStatus)
                    }
                    disabled={isSubmitting}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todo">
                        {t('taskFormDialog.statusOptions.todo')}
                      </SelectItem>
                      <SelectItem value="inprogress">
                        {t('taskFormDialog.statusOptions.inprogress')}
                      </SelectItem>
                      <SelectItem value="inreview">
                        {t('taskFormDialog.statusOptions.inreview')}
                      </SelectItem>
                      <SelectItem value="done">
                        {t('taskFormDialog.statusOptions.done')}
                      </SelectItem>
                      <SelectItem value="cancelled">
                        {t('taskFormDialog.statusOptions.cancelled')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>
          )}

          {/* Column dropdown in edit mode */}
          {editMode && (
            <form.Field name="column_id">
              {(field) => (
                <div className="space-y-2">
                  <Label htmlFor="task-column" className="text-sm font-medium">
                    Column
                  </Label>
                  <Select
                    value={field.state.value || 'none'}
                    onValueChange={(value) =>
                      field.handleChange(value === 'none' ? null : value)
                    }
                    disabled={isSubmitting || columnsLoading}
                  >
                    <SelectTrigger id="task-column">
                      <SelectValue placeholder="Select column" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No column</SelectItem>
                      {projectColumns
                        .sort((a, b) => a.position - b.position)
                        .map((col) => (
                          <SelectItem key={col.id} value={col.id}>
                            {col.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>
          )}

          {/* Create mode dropdowns */}
          {!editMode && (
            <form.Field name="autoStart" mode="array">
              {(autoStartField) => {
                const isSingleRepo = repoBranchConfigs.length === 1;
                return (
                  <div
                    className={cn(
                      'transition-opacity duration-200',
                      isSingleRepo ? '' : 'space-y-3',
                      autoStartField.state.value
                        ? 'opacity-100'
                        : 'opacity-0 pointer-events-none'
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <form.Field name="executorProfileId">
                        {(field) => (
                          <ExecutorProfileSelector
                            profiles={profiles}
                            selectedProfile={field.state.value}
                            onProfileSelect={(profile) =>
                              field.handleChange(profile)
                            }
                            disabled={
                              isSubmitting || !autoStartField.state.value
                            }
                            showLabel={false}
                            className="flex items-center gap-2 flex-row flex-[2] min-w-0"
                            itemClassName="flex-1 min-w-0"
                          />
                        )}
                      </form.Field>
                      {isSingleRepo && (
                        <form.Field name="repoBranches">
                          {(field) => {
                            const config = repoBranchConfigs[0];
                            const selectedBranch =
                              field.state.value.find(
                                (v) => v.repoId === config.repoId
                              )?.branch ?? config.targetBranch;
                            return (
                              <div
                                className={cn(
                                  'flex-1 min-w-0',
                                  isSubmitting &&
                                    'opacity-50 pointer-events-none'
                                )}
                              >
                                <BranchSelector
                                  branches={config.branches}
                                  selectedBranch={selectedBranch}
                                  onBranchSelect={(branch) => {
                                    field.handleChange([
                                      { repoId: config.repoId, branch },
                                    ]);
                                  }}
                                  placeholder={
                                    branchesLoading
                                      ? t('createAttemptDialog.loadingBranches')
                                      : t('createAttemptDialog.selectBranch')
                                  }
                                />
                              </div>
                            );
                          }}
                        </form.Field>
                      )}
                    </div>
                    {!isSingleRepo && (
                      <form.Field name="repoBranches">
                        {(field) => {
                          const configs = repoBranchConfigs.map((config) => ({
                            ...config,
                            targetBranch:
                              field.state.value.find(
                                (v) => v.repoId === config.repoId
                              )?.branch ?? config.targetBranch,
                          }));
                          return (
                            <RepoBranchSelector
                              configs={configs}
                              selectedRepoIds={selectedRepoIds}
                              onRepoToggle={toggleRepo}
                              onSelectAll={selectAll}
                              onSelectNone={selectNone}
                              onBranchChange={(repoId, branch) => {
                                const newValue = field.state.value.map((v) =>
                                  v.repoId === repoId ? { ...v, branch } : v
                                );
                                if (
                                  !newValue.find((v) => v.repoId === repoId)
                                ) {
                                  newValue.push({ repoId, branch });
                                }
                                field.handleChange(newValue);
                              }}
                              isLoading={branchesLoading}
                              showLabel={true}
                              className={cn(
                                isSubmitting && 'opacity-50 pointer-events-none'
                              )}
                            />
                          );
                        }}
                      </form.Field>
                    )}
                  </div>
                );
              }}
            </form.Field>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between gap-3">
            {/* Attach Image*/}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={dropzoneOpen}
                className="h-9 w-9 p-0 rounded-none"
                aria-label={t('taskFormDialog.attachImage')}
              >
                <ImageIcon className="h-4 w-4" />
              </Button>
            </div>

            {/* Autostart switch */}
            <div className="flex items-center gap-3">
              {!editMode && (
                <form.Field name="autoStart">
                  {(field) => (
                    <div className="flex items-center gap-2">
                      <Switch
                        id="autostart-switch"
                        checked={field.state.value}
                        onCheckedChange={(checked) =>
                          field.handleChange(checked)
                        }
                        disabled={isSubmitting}
                        className="data-[state=checked]:bg-gray-900 dark:data-[state=checked]:bg-gray-100"
                        aria-label={t('taskFormDialog.startLabel')}
                      />
                      <Label
                        htmlFor="autostart-switch"
                        className="text-sm cursor-pointer"
                      >
                        {t('taskFormDialog.startLabel')}
                      </Label>
                    </div>
                  )}
                </form.Field>
              )}

              {/* Create/Start/Update button*/}
              <form.Subscribe
                selector={(state) => ({
                  canSubmit: state.canSubmit,
                  isSubmitting: state.isSubmitting,
                  values: state.values,
                })}
              >
                {({ canSubmit, isSubmitting, values }) => {
                  const buttonText = editMode
                    ? isSubmitting
                      ? t('taskFormDialog.updating')
                      : t('taskFormDialog.updateTask')
                    : isSubmitting
                      ? values.autoStart
                        ? t('taskFormDialog.starting')
                        : t('taskFormDialog.creating')
                      : t('taskFormDialog.create');

                  return (
                    <Button onClick={form.handleSubmit} disabled={!canSubmit}>
                      {buttonText}
                    </Button>
                  );
                }}
              </form.Subscribe>
            </div>
          </div>
        </div>
      </Dialog>
      {showDiscardWarning && (
        <div className="fixed inset-0 z-[10000] flex items-start justify-center p-4 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setShowDiscardWarning(false)}
          />
          <div className="relative z-[10000] grid w-full max-w-lg gap-4 bg-primary p-6 shadow-lg duration-200 sm:rounded-lg my-8">
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <DialogTitle>
                    {t('taskFormDialog.discardDialog.title')}
                  </DialogTitle>
                </div>
                <DialogDescription className="text-left pt-2">
                  {t('taskFormDialog.discardDialog.description')}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={handleContinueEditing}>
                  {t('taskFormDialog.discardDialog.continueEditing')}
                </Button>
                <Button variant="destructive" onClick={handleDiscardChanges}>
                  {t('taskFormDialog.discardDialog.discardChanges')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </div>
        </div>
      )}
    </>
  );
});

export const TaskFormDialog = defineModal<TaskFormDialogProps, void>(
  TaskFormDialogImpl
);
